// Master-P3 Wave 1.3 — Bulk Regenerate Modal.
//
// Contract with backend:
//   POST /orchestrator/runs/{run_id}/regenerate/preview
//     → { affected_artifacts, estimated_calls, estimated_cost_range_eur,
//         would_supersede, would_expire_approvals }
//   POST /orchestrator/runs/{run_id}/regenerate
//     with { scope, artifact_ids, idempotency_key }
//     → 200 { superseded_count, expired_approvals_count,
//              steps_reset_count, next_action }
//     → 402 payment_required_* (missing/exhausted budget frame)
//     → 409 bulk_regeneration_already_in_progress
//     → 422 targeted_scope_requires_artifact_ids
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const styles = {
  overlay: { position: "fixed", inset: 0,
             background: "rgba(0,0,0,0.55)", zIndex: 1000,
             display: "flex", alignItems: "center",
             justifyContent: "center" },
  modal: { background: "#111", color: "#eee", borderRadius: 8,
           padding: 20, width: "min(640px, 92vw)",
           maxHeight: "88vh", overflow: "auto",
           border: "1px solid #333" },
  h: { margin: "0 0 10px", fontSize: 18 },
  row: { display: "flex", justifyContent: "space-between",
         alignItems: "center", padding: "6px 0",
         borderBottom: "1px solid #222" },
  chip: { fontSize: 11, padding: "1px 6px", borderRadius: 10,
          background: "#333" },
  danger: { color: "#e8b400" },
  actions: { display: "flex", gap: 8, justifyContent: "flex-end",
             marginTop: 14 },
  btn: (variant = "default") => ({
    padding: "8px 14px", borderRadius: 6,
    border: "1px solid #555",
    background: variant === "danger" ? "#8b1a1a"
      : variant === "primary" ? "#0e4429" : "#222",
    color: "#fff", cursor: "pointer" }),
};

// Random-enough idempotency key. Not cryptographic — just needs to
// survive a double-click on the confirm button.
function newIdempotencyKey() {
  return "bulk-" + Date.now().toString(36) + "-"
       + Math.random().toString(36).slice(2, 8);
}

export default function BulkRegenerateModal({
  runId, briefVersion, open, onClose, onDone,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState("full");
  const [selectedIds, setSelectedIds] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !runId) return;
    setError(null); setPreview(null);
    setScope("full"); setSelectedIds({});
    setLoading(true);
    api.post(`/orchestrator/runs/${runId}/regenerate/preview`,
              { scope: "full", artifact_ids: [] })
      .then((r) => { setPreview(r.data); })
      .catch((e) => {
        setError(e.response?.data?.detail?.error || String(e));
      })
      .finally(() => setLoading(false));
  }, [open, runId]);

  const affected = preview?.affected_artifacts || [];
  const targetedIds = useMemo(
    () => Object.keys(selectedIds).filter((k) => selectedIds[k]),
    [selectedIds]);

  const confirm = async () => {
    setSubmitting(true); setError(null);
    const body = {
      scope,
      artifact_ids: scope === "targeted" ? targetedIds : [],
      idempotency_key: newIdempotencyKey(),
    };
    try {
      const r = await api.post(
        `/orchestrator/runs/${runId}/regenerate`, body);
      toast.success(
        `Regeneriert: ${r.data.superseded_count} Artefakte, `
      + `${r.data.expired_approvals_count} Freigaben als veraltet, `
      + `${r.data.steps_reset_count} Schritte zurückgesetzt.`);
      onDone && onDone(r.data);
      onClose && onClose();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || {};
      if (status === 402) {
        setError("Kein aktives Budget-Frame — Regenerieren nicht möglich.");
      } else if (status === 409) {
        setError("Eine Regenerierung läuft bereits für diesen Run.");
      } else if (status === 422) {
        setError("Bitte mindestens ein Artefakt auswählen.");
      } else {
        setError(detail.error || String(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} data-testid="bulk-regenerate-modal"
          role="dialog" aria-modal="true">
      <div style={styles.modal}>
        <h3 style={styles.h}>Alle regenerieren — Vorschau</h3>
        <p style={{ fontSize: 13, color: "#aaa", marginBottom: 8 }}>
          Aktive Brief-Version: <b>v{briefVersion}</b> · Run:{" "}
          <code data-testid="bulk-regen-run-id">{runId}</code>
        </p>

        {loading && (
          <p data-testid="bulk-regen-loading">Vorschau wird geladen…</p>
        )}
        {error && (
          <div data-testid="bulk-regen-error"
                style={{ background: "#8b1a1a", color: "#fff",
                          padding: 8, borderRadius: 4 }}>
            {error}
          </div>
        )}

        {preview && (
          <>
            <div data-testid="bulk-regen-summary"
                  style={{ marginTop: 10, marginBottom: 10 }}>
              <div style={styles.row}>
                <span>Betroffene Artefakte</span>
                <span style={styles.chip}
                       data-testid="bulk-regen-affected-count">
                  {affected.length}
                </span>
              </div>
              <div style={styles.row}>
                <span>Geschätzte Aufrufe (Text-LLM · Bild)</span>
                <span style={styles.chip}
                       data-testid="bulk-regen-estimated-calls">
                  {preview.estimated_calls?.text_llm ?? 0}
                  {" · "}
                  {preview.estimated_calls?.image_gen ?? 0}
                </span>
              </div>
              <div style={styles.row}>
                <span>Betroffene Freigaben</span>
                <span style={{ ...styles.chip, ...styles.danger }}
                       data-testid="bulk-regen-expiring-approvals">
                  {(preview.would_expire_approvals || []).length}
                </span>
              </div>
              <div style={{ ...styles.row, fontSize: 12,
                              color: "#aaa" }}>
                <span>Kostenschätzung (nur best-effort)</span>
                <span data-testid="bulk-regen-cost-range">
                  ~ {preview.estimated_cost_range_eur?.low?.toFixed(2)}
                  {" – "}
                  {preview.estimated_cost_range_eur?.high?.toFixed(2)} EUR
                  {preview.estimated_cost_range_eur?.unknown_cost_flag
                    ? " (Preis nicht garantiert)" : ""}
                </span>
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <label style={{ display: "block", marginBottom: 6 }}>
                <input type="radio" name="regen-scope"
                        value="full" checked={scope === "full"}
                        onChange={() => setScope("full")}
                        data-testid="bulk-regen-scope-full" />
                {" "}Alle regenerieren ({affected.length})
              </label>
              <label style={{ display: "block", marginBottom: 6 }}>
                <input type="radio" name="regen-scope"
                        value="targeted" checked={scope === "targeted"}
                        onChange={() => setScope("targeted")}
                        data-testid="bulk-regen-scope-targeted" />
                {" "}Nur ausgewählte
              </label>
              {scope === "targeted" && (
                <div data-testid="bulk-regen-target-picker"
                      style={{ marginLeft: 22, maxHeight: 180,
                                overflow: "auto" }}>
                  {affected.map((a) => (
                    <label key={a.id}
                            style={{ display: "block",
                                      fontSize: 12,
                                      padding: "2px 0" }}>
                      <input type="checkbox"
                              checked={!!selectedIds[a.id]}
                              data-testid={`bulk-regen-pick-${a.id}`}
                              onChange={(e) => setSelectedIds(
                                (s) => ({ ...s,
                                            [a.id]: e.target.checked }))} />
                      {" "}{a.artifact_type} · {a.id}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div style={styles.actions}>
          <button style={styles.btn()} onClick={onClose}
                    data-testid="bulk-regen-cancel">
            Abbrechen
          </button>
          <button style={styles.btn("danger")}
                    disabled={submitting || !preview || loading
                              || (scope === "targeted"
                                    && targetedIds.length === 0)}
                    onClick={confirm}
                    data-testid="bulk-regen-confirm">
            Fortfahren
            {scope === "full" ? ` (${affected.length})`
                              : ` (${targetedIds.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
